import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Play, Eye, Trash2, Loader2, Bot, Terminal, Shield, Settings, Cpu, Target, RefreshCw, Upload, Globe, FileJson } from 'lucide-react';
import { startAIPentest, getPentestSessions, deletePentestSession, rescanPentestSession, getAIProviders, getAIConfig, updateAIConfig, getPentestTools, scrapeBugBountyScope, importBurpConfig } from '@/services/litesabrePro';
import { toast } from 'sonner';

const SEVERITY_STYLES = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-blue-500 text-white',
  info: 'bg-gray-400 text-white',
};

const AIPentestDashboard = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPentest, setShowNewPentest] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [starting, setStarting] = useState(false);
  const [aiConfig, setAiConfig] = useState(null);
  const [providers, setProviders] = useState({});
  const [tools, setTools] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const proBasePath = location.pathname.startsWith('/litesabre-pro') ? '/litesabre-pro' : '/grc/litesabre-pro';

  // New pentest form
  const [pentestForm, setPentestForm] = useState({
    target: '',
    scope: 'full',
    name: '',
    max_steps: 8,
    include_subdomains: false,
    provider: '',
    model: '',
    api_key: '',
    hackerone_url: '',
  });

  const [scrapingScope, setScrapingScope] = useState(false);
  const [scrapedScope, setScrapedScope] = useState(null);
  const [importingBurp, setImportingBurp] = useState(false);
  const [rescanning, setRescanning] = useState(null);

  // AI config form
  const [configForm, setConfigForm] = useState({
    provider: 'openai',
    model: 'gpt-4o',
    api_key: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sessionsData, configData, providersData, toolsData] = await Promise.all([
        getPentestSessions(),
        getAIConfig(),
        getAIProviders(),
        getPentestTools(),
      ]);
      setSessions(sessionsData?.sessions || []);
      setAiConfig(configData);
      setProviders(providersData?.providers || {});
      setTools(toolsData?.tools || []);
      if (configData) {
        setConfigForm({
          provider: configData.provider || 'openai',
          model: configData.model || 'gpt-4o',
          api_key: '',
        });
      }
    } catch (err) {
      toast.error('Failed to load pentest data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPentest = async () => {
    if (!pentestForm.target) {
      toast.error('Target is required');
      return;
    }
    // Require API key either in form or saved config
    if (!pentestForm.api_key && !aiConfig?.has_api_key) {
      toast.error('API key is required. Configure it in AI Config or provide one here.');
      return;
    }
    try {
      setStarting(true);
      const payload = {
        target: pentestForm.target,
        scope: pentestForm.scope,
        name: pentestForm.name || `AI Pentest: ${pentestForm.target}`,
        max_steps: pentestForm.max_steps,
        include_subdomains: pentestForm.include_subdomains,
      };
      if (pentestForm.hackerone_url) {
        payload.hackerone_url = pentestForm.hackerone_url;
      }
      // Include additional targets from scraped scope
      if (scrapedScope?.target_urls?.length > 0) {
        payload.targets = scrapedScope.target_urls;
      }
      if (pentestForm.provider && pentestForm.api_key) {
        payload.ai_config = {
          provider: pentestForm.provider,
          model: pentestForm.model,
          api_key: pentestForm.api_key,
        };
      }
      const result = await startAIPentest(payload);
      const targetCount = result?.targets_count || 1;
      toast.success(`AI Pentest started for ${targetCount > 1 ? `${targetCount} targets` : pentestForm.target}`);
      setShowNewPentest(false);
      setPentestForm({ target: '', scope: 'full', name: '', max_steps: 8, include_subdomains: false, provider: '', model: '', api_key: '', hackerone_url: '' });
      setScrapedScope(null);
      await loadData();
      if (result?.session_id) {
        navigate(`${proBasePath}/pentest/${result.session_id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start pentest');
    } finally {
      setStarting(false);
    }
  };

  const handleScrapeScope = async () => {
    if (!pentestForm.hackerone_url) {
      toast.error('Enter a HackerOne URL first');
      return;
    }
    try {
      setScrapingScope(true);
      const data = await scrapeBugBountyScope(pentestForm.hackerone_url);
      setScrapedScope(data);
      // Auto-populate first target URL if target field is empty
      if (!pentestForm.target && data.target_urls?.length > 0) {
        setPentestForm(prev => ({ ...prev, target: data.target_urls[0] }));
      }
      const targetCount = data.target_urls?.length || 0;
      toast.success(`Scraped ${data.summary?.total_assets || 0} assets, ${targetCount} target URLs from ${data.program}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to scrape scope');
    } finally {
      setScrapingScope(false);
    }
  };

  const handleBurpFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Please upload a Burp Suite .json config file');
      return;
    }
    try {
      setImportingBurp(true);
      const text = await file.text();
      const configJson = JSON.parse(text);
      const data = await importBurpConfig(configJson);
      setScrapedScope(data);
      if (!pentestForm.target && data.target_urls?.length > 0) {
        setPentestForm(prev => ({ ...prev, target: data.target_urls[0] }));
      }
      toast.success(`Imported ${data.summary?.total_assets || 0} targets from Burp Suite config`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse Burp Suite config');
    } finally {
      setImportingBurp(false);
      event.target.value = '';
    }
  };

  const viewSession = (sessionId) => {
    navigate(`${proBasePath}/pentest/${sessionId}`);
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await deletePentestSession(sessionId);
      toast.success('Session deleted');
      loadData();
    } catch {
      toast.error('Failed to delete session');
    }
  };

  const handleRescan = async (sessionId, e) => {
    e.stopPropagation();
    try {
      setRescanning(sessionId);
      const result = await rescanPentestSession(sessionId);
      toast.success(`Rescan started for ${result.target}`);
      await loadData();
      if (result?.new_session_id) {
        navigate(`${proBasePath}/pentest/${result.new_session_id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start rescan');
    } finally {
      setRescanning(null);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await updateAIConfig(configForm);
      toast.success('AI configuration saved');
      setShowConfig(false);
      const configData = await getAIConfig();
      setAiConfig(configData);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save config');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-emerald-600 text-white',
      running: 'bg-blue-500 text-white animate-pulse',
      pending: 'bg-yellow-500 text-black',
      failed: 'bg-red-600 text-white',
    };
    return <Badge className={styles[status] || 'bg-gray-400'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-pentest-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-6 h-6 text-indigo-600" />
            AI Penetration Testing Agent
          </h2>
          <p className="text-sm text-gray-500">Autonomous AI-powered security assessments using simulated Kali Linux tools</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} data-testid="ai-config-btn">
            <Settings className="w-4 h-4 mr-1" /> AI Config
          </Button>
          <Button onClick={() => setShowNewPentest(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="start-pentest-btn">
            <Play className="w-4 h-4 mr-1" /> New Pentest
          </Button>
        </div>
      </div>

      {/* AI Config Status */}
      {aiConfig && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-3 flex items-center gap-4 text-sm">
            <Cpu className="w-4 h-4 text-slate-500" />
            <span className="text-slate-600">AI Engine:</span>
            <Badge variant="outline" className="font-mono">{aiConfig.provider} / {aiConfig.model}</Badge>
            <span className="text-slate-400">|</span>
            <span className={aiConfig.has_api_key ? 'text-emerald-600' : 'text-amber-600'}>
              {aiConfig.has_api_key ? 'API key configured' : 'No API key — configure in AI Config'}
            </span>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="sessions">
        <TabsList>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="tools">Available Tools</TabsTrigger>
        </TabsList>

        {/* Sessions List */}
        <TabsContent value="sessions" className="mt-4">
          {sessions.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center text-gray-500">
                <Bot className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No pentest sessions yet</p>
                <p className="text-sm">Start your first AI-powered penetration test</p>
                <Button className="mt-4 bg-indigo-600" onClick={() => setShowNewPentest(true)}>
                  <Play className="w-4 h-4 mr-1" /> Start Pentest
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>AI Model</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Steps</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((s) => (
                  <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {s.target}
                      {s.targets?.length > 1 && (
                        <Badge variant="outline" className="ml-2 text-xs">{s.targets.length} targets</Badge>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline">{s.scope}</Badge></TableCell>
                    <TableCell className="text-xs font-mono">{s.ai_config?.provider}/{s.ai_config?.model}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        {getStatusBadge(s.status)}
                        {s.status === 'failed' && s.error && (
                          <span className="text-xs text-red-500 mt-0.5 max-w-[200px] truncate" title={s.error}>{s.error.length > 50 ? s.error.slice(0, 50) + '...' : s.error}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{s.current_step || 0}/{s.max_steps || 8}</TableCell>
                    <TableCell className="text-sm">{new Date(s.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => viewSession(s.id)} data-testid={`view-session-${s.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {(s.status === 'completed' || s.status === 'failed') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-indigo-500"
                            onClick={(e) => handleRescan(s.id, e)}
                            disabled={rescanning === s.id}
                            data-testid={`rescan-session-${s.id}`}
                          >
                            {rescanning === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteSession(s.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        {/* Available Tools */}
        <TabsContent value="tools" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tools.map((tool) => (
              <Card key={tool.name} className="hover:border-indigo-300 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Terminal className="w-5 h-5 text-emerald-500" />
                    <span className="font-mono font-bold text-sm">{tool.name}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{tool.description}</p>
                  <code className="text-xs text-gray-400 font-mono">{tool.usage}</code>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* New Pentest Dialog */}
      <Dialog open={showNewPentest} onOpenChange={setShowNewPentest}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-indigo-600" /> Start AI Pentest</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Target URL / IP</Label>
              <Input value={pentestForm.target} onChange={(e) => setPentestForm({ ...pentestForm, target: e.target.value })} placeholder="e.g., example.com or 192.168.1.1" data-testid="pentest-target-input" />
            </div>
            <div>
              <Label>Scan Name (optional)</Label>
              <Input value={pentestForm.name} onChange={(e) => setPentestForm({ ...pentestForm, name: e.target.value })} placeholder="Auto-generated if empty" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scope</Label>
                <Select value={pentestForm.scope} onValueChange={(v) => setPentestForm({ ...pentestForm, scope: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Assessment</SelectItem>
                    <SelectItem value="web">Web Only</SelectItem>
                    <SelectItem value="network">Network Only</SelectItem>
                    <SelectItem value="api">API Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max Steps</Label>
                <Select value={String(pentestForm.max_steps)} onValueChange={(v) => setPentestForm({ ...pentestForm, max_steps: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 steps</SelectItem>
                    <SelectItem value="8">8 steps (recommended)</SelectItem>
                    <SelectItem value="12">12 steps (thorough)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bug Bounty Scope (multi-platform) */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="pentest_include_subdomains"
                checked={pentestForm.include_subdomains}
                onChange={(e) => setPentestForm({ ...pentestForm, include_subdomains: e.target.checked })}
                className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                data-testid="pentest-include-subdomains"
              />
              <Label htmlFor="pentest_include_subdomains" className="cursor-pointer">
                Include Subdomains
                <span className="text-xs text-gray-500 ml-2">(Enumerate and scan discovered subdomains)</span>
              </Label>
            </div>
            <div className="border-t pt-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Bug Bounty Scope (optional)</p>
              <div className="flex gap-2">
                <Input
                  value={pentestForm.hackerone_url}
                  onChange={(e) => setPentestForm({ ...pentestForm, hackerone_url: e.target.value })}
                  placeholder="HackerOne, Bugcrowd, Intigriti, YesWeHack, or Synack URL"
                  className="flex-1"
                  data-testid="hackerone-url-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleScrapeScope}
                  disabled={scrapingScope || !pentestForm.hackerone_url}
                  data-testid="scrape-scope-btn"
                >
                  {scrapingScope ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Globe className="w-3 h-3 mr-1" /> Fetch</>}
                </Button>
              </div>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className="text-xs text-gray-400">Supported:</span>
                {['hackerone.com', 'bugcrowd.com', 'intigriti.com', 'yeswehack.com', 'synack.com'].map(p => (
                  <span key={p} className="text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{p}</span>
                ))}
              </div>
              {/* Burp Suite JSON Import */}
              <div className="flex items-center gap-2 mt-2">
                <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-md cursor-pointer hover:bg-slate-50 transition-colors" data-testid="burp-upload-label">
                  {importingBurp ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileJson className="w-3 h-3" />}
                  <span>Import Burp Suite Config</span>
                  <input type="file" accept=".json" onChange={handleBurpFileUpload} className="hidden" data-testid="burp-file-input" />
                </label>
                <span className="text-xs text-gray-400">or paste HackerOne URL above</span>
              </div>
              {/* Scraped Scope Display */}
              {scrapedScope && (
                <div className="mt-2 bg-slate-50 rounded-lg p-3 text-xs space-y-2 border border-slate-200" data-testid="scraped-scope-display">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-800">Program: {scrapedScope.program}</p>
                      {scrapedScope.platform && (
                        <Badge variant="secondary" className="text-xs capitalize">{scrapedScope.platform}</Badge>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" className="h-5 text-xs text-red-500 p-0" onClick={() => setScrapedScope(null)}>Clear</Button>
                  </div>
                  <div className="flex gap-3 text-slate-600">
                    <span>{scrapedScope.summary?.in_scope_count} in-scope</span>
                    <span>{scrapedScope.summary?.bounty_eligible} bounty-eligible</span>
                    {scrapedScope.summary?.target_url_count > 0 && (
                      <span className="text-indigo-600 font-medium">{scrapedScope.summary.target_url_count} target URLs</span>
                    )}
                  </div>
                  {/* Target URLs */}
                  {scrapedScope.target_urls?.length > 0 && (
                    <div data-testid="target-urls-list">
                      <p className="font-medium text-slate-700 mt-1 mb-1">Target URLs ({scrapedScope.target_urls.length}):</p>
                      <div className="flex flex-wrap gap-1">
                        {scrapedScope.target_urls.slice(0, 8).map((url, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className="text-xs cursor-pointer hover:bg-indigo-50"
                            onClick={() => setPentestForm(prev => ({ ...prev, target: url }))}
                          >
                            {url.length > 35 ? url.slice(0, 35) + '...' : url}
                          </Badge>
                        ))}
                        {scrapedScope.target_urls.length > 8 && (
                          <Badge variant="outline" className="text-xs">+{scrapedScope.target_urls.length - 8} more</Badge>
                        )}
                      </div>
                    </div>
                  )}
                  {/* In-scope assets */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {scrapedScope.in_scope?.slice(0, 5).map((a, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{a.identifier?.length > 30 ? a.identifier.slice(0, 30) + '...' : a.identifier}</Badge>
                    ))}
                    {scrapedScope.in_scope?.length > 5 && <Badge variant="outline" className="text-xs">+{scrapedScope.in_scope.length - 5} more</Badge>}
                  </div>
                  {/* Program guidelines */}
                  {scrapedScope.guidelines?.policy_text && (
                    <div className="mt-1 p-2 bg-white rounded border border-slate-100" data-testid="program-guidelines">
                      <p className="font-medium text-slate-700 text-xs">Program Guidelines:</p>
                      <p className="text-slate-500 text-xs mt-0.5">{scrapedScope.guidelines.policy_text.slice(0, 200)}{scrapedScope.guidelines.policy_text.length > 200 ? '...' : ''}</p>
                    </div>
                  )}
                  {/* Burp exclusions */}
                  {scrapedScope.burp_scope?.exclude_hosts?.length > 0 && (
                    <div className="mt-1">
                      <p className="font-medium text-red-600 text-xs">Excluded ({scrapedScope.burp_scope.exclude_hosts.length}):</p>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {scrapedScope.burp_scope.exclude_hosts.slice(0, 3).map((h, i) => (
                          <Badge key={i} variant="destructive" className="text-xs">{h.length > 30 ? h.slice(0, 30) + '...' : h}</Badge>
                        ))}
                        {scrapedScope.burp_scope.exclude_hosts.length > 3 && <Badge variant="destructive" className="text-xs">+{scrapedScope.burp_scope.exclude_hosts.length - 3} more</Badge>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* AI Engine Override */}
            <div className="border-t pt-3">
              <p className="text-sm font-medium text-gray-700 mb-2">AI Engine (override per-scan)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Provider</Label>
                  <Select value={pentestForm.provider || "default"} onValueChange={(v) => setPentestForm({ ...pentestForm, provider: v === "default" ? "" : v, model: '' })}>
                    <SelectTrigger><SelectValue placeholder="Use saved config" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Use saved config</SelectItem>
                      {Object.entries(providers).map(([key, info]) => (
                        <SelectItem key={key} value={key}>{info.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Model</Label>
                  <Select value={pentestForm.model || "none"} onValueChange={(v) => setPentestForm({ ...pentestForm, model: v === "none" ? "" : v })} disabled={!pentestForm.provider}>
                    <SelectTrigger><SelectValue placeholder="Select model" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select model</SelectItem>
                      {pentestForm.provider && providers[pentestForm.provider]?.models?.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {pentestForm.provider && (
                <div className="mt-2">
                  <Label className="text-xs">API Key (required)</Label>
                  <Input type="password" value={pentestForm.api_key} onChange={(e) => setPentestForm({ ...pentestForm, api_key: e.target.value })} placeholder="Enter your API key" data-testid="pentest-api-key-input" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewPentest(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleStartPentest} disabled={starting} data-testid="confirm-start-pentest">
              {starting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
              Start Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5" /> AI Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>AI Provider</Label>
              <Select value={configForm.provider} onValueChange={(v) => setConfigForm({ ...configForm, provider: v, model: providers[v]?.default_model || '' })}>
                <SelectTrigger data-testid="config-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(providers).map(([key, info]) => (
                    <SelectItem key={key} value={key}>{info.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Model</Label>
              <Select value={configForm.model} onValueChange={(v) => setConfigForm({ ...configForm, model: v })}>
                <SelectTrigger data-testid="config-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {providers[configForm.provider]?.models?.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>API Key (required)</Label>
              <Input type="password" value={configForm.api_key} onChange={(e) => setConfigForm({ ...configForm, api_key: e.target.value })} placeholder="Enter your provider API key" data-testid="config-api-key-input" />
              <p className="text-xs text-gray-400 mt-1">Your API key for the selected provider. Required to run pentests.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSaveConfig} data-testid="save-ai-config">Save Config</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIPentestDashboard;

